import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import {
  AUDIO_MAX_MINUTES,
  NOTE_MAX_CHARS,
  type EventInfo,
  type MeResponse,
  type NoteInfo,
  type OutlookStatus,
  type ProjectSummary,
} from "@handvero/shared";
import { isOffline, queueRequest } from "./offlineQueue";
import { api, errorText, upload } from "../api";
import { CalendarFeedPanel, ShareEventSheet } from "./CalendarSharing";
import { EmptyState, Icon, Island, Message, Sheet, useUnauthorizedRedirect } from "./ui";

/* ---------- Hilfen ---------- */

/** Extras wie das Teilen von Terminen gibt es nur in der Windows-App; die Handy-App bleibt schlicht. */
function useDesk() {
  return useLocation().pathname.startsWith("/desktop");
}

function useProjects(enabled: boolean) {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  useEffect(() => {
    if (enabled) api.get<ProjectSummary[]>("/api/projects").then(setProjects).catch(() => undefined);
  }, [enabled]);
  return projects;
}

function ProjectSelect({ projects, value, onChange }: { projects: ProjectSummary[]; value: string; onChange: (v: string) => void }) {
  return (
    <label>
      Projekt (freiwillig)
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Kein Projekt</option>
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function clock(seconds: number) {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function when(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  const time = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === today.toDateString()) return `Heute, ${time}`;
  if (d.toDateString() === yesterday.toDateString()) return `Gestern, ${time}`;
  return `${d.toLocaleDateString("de-DE", { day: "numeric", month: "short", year: d.getFullYear() === today.getFullYear() ? undefined : "numeric" })}, ${time}`;
}

/* ---------- Sprachmemo abspielen ---------- */

/** Eigener Player: großer Knopf, Wellenform-Balken als Fortschritt, Zeit. */
export function VoicePlayer({ src, seconds, seed }: { src: string; seconds: number | null; seed: string }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(seconds ?? 0);
  const bars = useMemo(() => {
    // Gleichbleibendes Muster je Notiz, damit jedes Memo ein eigenes „Gesicht“ hat.
    let h = 0;
    for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
    return Array.from({ length: 32 }, () => {
      h = (h * 1103515245 + 12345) >>> 0;
      return 0.25 + ((h >>> 16) % 1000) / 1333;
    });
  }, [seed]);
  const share = dur > 0 ? Math.min(1, pos / dur) : 0;

  function toggle() {
    const a = audio.current;
    if (!a) return;
    if (a.paused) void a.play();
    else a.pause();
  }

  function seek(e: React.MouseEvent<HTMLDivElement>) {
    const a = audio.current;
    if (!a || !dur) return;
    const r = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - r.left) / r.width) * dur;
  }

  return (
    <div className={`voice${playing ? " playing" : ""}`}>
      <button type="button" className="voice-btn" onClick={toggle} aria-label={playing ? "Pause" : "Abspielen"}>
        {playing ? (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="7" y="6" width="3.5" height="12" rx="1" fill="currentColor" />
            <rect x="13.5" y="6" width="3.5" height="12" rx="1" fill="currentColor" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
          </svg>
        )}
      </button>
      <div className="voice-bars" onClick={seek} role="presentation">
        {bars.map((b, i) => (
          <i key={i} className={i / bars.length < share ? "on" : undefined} style={{ height: `${Math.round(b * 100)}%` }} />
        ))}
      </div>
      <span className="voice-time">{clock(playing || pos ? pos : dur)}</span>
      <audio
        ref={audio}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPos(0);
        }}
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => Number.isFinite(e.currentTarget.duration) && setDur(e.currentTarget.duration)}
      />
    </div>
  );
}

/* ---------- Sprachmemo aufnehmen ---------- */

/** Formate, die der Server annimmt, in der Reihenfolge der Vorliebe. */
const RECORD_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];

interface Recording {
  blob: Blob;
  seconds: number;
  url: string;
}

export function useRecorder() {
  const [state, setState] = useState<"idle" | "recording" | "done">("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const [result, setResult] = useState<Recording | null>(null);
  const rec = useRef<MediaRecorder | null>(null);
  const started = useRef(0);
  const timer = useRef<number | undefined>(undefined);

  const stop = useCallback(() => {
    if (rec.current && rec.current.state !== "inactive") rec.current.stop();
    window.clearInterval(timer.current);
  }, []);

  const start = useCallback(async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("Dieser Browser kann keine Sprachmemos aufnehmen. Bitte nutzen Sie einen aktuellen Browser oder schreiben Sie die Notiz.");
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError("Kein Zugriff auf das Mikrofon. Bitte erlauben Sie HandVero in den Einstellungen Ihres Browsers den Zugriff auf das Mikrofon.");
      return;
    }
    const mimeType = RECORD_TYPES.find((t) => MediaRecorder.isTypeSupported(t));
    const r = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const chunks: Blob[] = [];
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks, { type: (r.mimeType || mimeType || "audio/webm").split(";")[0] });
      const seconds = (Date.now() - started.current) / 1000;
      setResult({ blob, seconds, url: URL.createObjectURL(blob) });
      setState("done");
    };
    rec.current = r;
    started.current = Date.now();
    setElapsed(0);
    r.start(1000);
    setState("recording");
    timer.current = window.setInterval(() => {
      const s = (Date.now() - started.current) / 1000;
      setElapsed(s);
      // Längere Aufnahmen automatisch beenden, damit die Datei handlich bleibt.
      if (s >= AUDIO_MAX_MINUTES * 60) stop();
    }, 250);
  }, [stop]);

  const reset = useCallback(() => {
    if (result) URL.revokeObjectURL(result.url);
    setResult(null);
    setState("idle");
    setElapsed(0);
  }, [result]);

  useEffect(
    () => () => {
      window.clearInterval(timer.current);
      if (rec.current && rec.current.state !== "inactive") rec.current.stop();
    },
    [],
  );

  return { state, elapsed, error, result, start, stop, reset };
}

/* ---------- Notizen ---------- */

export function NotesView({ projectId, projectName, nav }: { projectId?: string; projectName?: string; nav?: ReactNode }) {
  const onAuth = useUnauthorizedRedirect();
  const [notes, setNotes] = useState<NoteInfo[] | null>(null);
  const [error, setError] = useState("");
  const [sheet, setSheet] = useState<{ mode: "text" | "voice"; note?: NoteInfo } | null>(null);
  const [menu, setMenu] = useState<NoteInfo | null>(null);

  const load = useCallback(() => {
    api
      .get<NoteInfo[]>(projectId ? `/api/notes?projectId=${projectId}` : "/api/notes")
      .then(setNotes)
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [projectId, onAuth]);
  useEffect(load, [load]);

  async function remove(n: NoteInfo) {
    setMenu(null);
    if (!window.confirm(n.hasAudio ? "Diese Notiz samt Sprachmemo endgültig löschen?" : "Diese Notiz endgültig löschen?")) return;
    try {
      await api.del(`/api/notes/${n.id}`);
      setNotes((all) => (all ?? []).filter((x) => x.id !== n.id));
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <>
      <main className="app-main">
        <Message>{error}</Message>
        {!notes && !error && <div className="skel" role="status" aria-label="Wird geladen"><i /><i /></div>}
        {notes?.length === 0 && (
          <EmptyState
            title="Noch keine Notizen"
            text={`Halten Sie Absprachen, Aufmaße oder Ideen fest. Mit dem Mikrofon sprechen Sie eine Notiz einfach ein${projectName ? `, sie gehört dann zu „${projectName}“.` : "."}`}
            words={["Sagen", "Merken"]}
          />
        )}
        <ul className="notes-list">
          {notes?.map((n, i) => (
            <li key={n.id} className={`note-card t${i % 3}`} style={{ "--i": i } as React.CSSProperties}>
              <div className="note-meta">
                <span>
                  {when(n.createdAt)}
                  {n.createdBy && ` · ${n.createdBy}`}
                </span>
                {n.canEdit && (
                  <button className="ab-btn small" onClick={() => setMenu(n)} aria-label="Notiz bearbeiten">
                    <Icon name="more" />
                  </button>
                )}
              </div>
              {n.hasAudio && (
                <VoicePlayer src={`/api/notes/${n.id}/audio`} seconds={n.audioSeconds} seed={n.id} />
              )}
              {n.body && <p className="note-body">{n.body}</p>}
              {!projectId && n.projectId && (
                <Link className="chip" to={`/app/p/${n.projectId}/notizen`}>
                  <Icon name="folder" />
                  {n.projectName}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </main>

      <Island>
        {nav}
        <button className="is-tab" onClick={() => setSheet({ mode: "text" })} aria-label="Notiz schreiben">
          <Icon name="note" />
          <span>Schreiben</span>
        </button>
        <button className="is-action" onClick={() => setSheet({ mode: "voice" })} aria-label="Sprachmemo aufnehmen">
          <Icon name="mic" />
        </button>
      </Island>

      {sheet && (
        <NoteSheet
          mode={sheet.mode}
          note={sheet.note}
          fixedProjectId={projectId}
          onClose={() => setSheet(null)}
          onSaved={(saved) => {
            setSheet(null);
            setNotes((all) => {
              const rest = (all ?? []).filter((x) => x.id !== saved.id);
              if (projectId && saved.projectId !== projectId) return rest;
              return sheet.note ? (all ?? []).map((x) => (x.id === saved.id ? saved : x)) : [saved, ...rest];
            });
          }}
        />
      )}
      {menu && (
        <Sheet title="Notiz" onClose={() => setMenu(null)}>
          <div className="menu">
            <button
              onClick={() => {
                setSheet({ mode: "text", note: menu });
                setMenu(null);
              }}
            >
              Bearbeiten
            </button>
            <button className="danger" onClick={() => void remove(menu)}>
              Löschen
            </button>
          </div>
        </Sheet>
      )}
    </>
  );
}

function NoteSheet(props: {
  mode: "text" | "voice";
  note?: NoteInfo;
  fixedProjectId?: string;
  onClose: () => void;
  onSaved: (n: NoteInfo) => void;
}) {
  const { note, fixedProjectId } = props;
  const projects = useProjects(!fixedProjectId);
  const [body, setBody] = useState(note?.body ?? "");
  const [projectId, setProjectId] = useState(note?.projectId ?? fixedProjectId ?? "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const recorder = useRecorder();
  const { start } = recorder;
  const autoStarted = useRef(false);

  // Beim Antippen des Mikrofons gleich mit der Aufnahme beginnen.
  useEffect(() => {
    if (props.mode === "voice" && !autoStarted.current) {
      autoStarted.current = true;
      void start();
    }
  }, [props.mode, start]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      let saved: NoteInfo;
      if (note) {
        saved = await api.patch<NoteInfo>(`/api/notes/${note.id}`, { body, projectId: projectId || null });
      } else if (recorder.result) {
        const form = new FormData();
        form.append("body", body);
        if (projectId) form.append("projectId", projectId);
        form.append("seconds", String(Math.round(recorder.result.seconds)));
        const ext = recorder.result.blob.type.includes("mp4") ? "m4a" : recorder.result.blob.type.includes("ogg") ? "ogg" : "webm";
        form.append("audio", recorder.result.blob, `sprachmemo.${ext}`);
        saved = await upload<NoteInfo>("/api/notes", form);
      } else {
        saved = await api.post<NoteInfo>("/api/notes", { body, projectId: projectId || null });
      }
      props.onSaved(saved);
    } catch (err) {
      if (isOffline(err) && !recorder.result) {
        // Ohne Netz: auf dem Gerät merken und später senden. Sprachmemos brauchen dafür noch Verbindung.
        try {
          await queueRequest(note ? { method: "PATCH", url: `/api/notes/${note.id}`, body: { body, projectId: projectId || null }, label: "Notiz" } : { method: "POST", url: "/api/notes", body: { body, projectId: projectId || null }, label: "Notiz" });
          props.onClose();
          return;
        } catch {
          /* Gerätespeicher nicht verfügbar: normale Fehlermeldung */
        }
      }
      setError(isOffline(err) && recorder.result ? "Kein Netz. Ein Sprachmemo lässt sich erst mit Verbindung speichern. Bitte versuchen Sie es noch einmal, sobald Sie wieder Netz haben." : errorText(err));
      setBusy(false);
    }
  }

  const voice = props.mode === "voice" && !note;
  const canSave = !busy && recorder.state !== "recording" && (body.trim().length > 0 || !!recorder.result);

  return (
    <Sheet title={note ? "Notiz bearbeiten" : voice ? "Sprachmemo" : "Neue Notiz"} onClose={props.onClose}>
      <form className="app-form" onSubmit={save}>
        {voice && (
          <div className="recorder">
            {recorder.state === "recording" && (
              <>
                <div className="rec-pulse" aria-hidden="true" />
                <b className="rec-time">{clock(recorder.elapsed)}</b>
                <span className="muted-s">Aufnahme läuft, höchstens {AUDIO_MAX_MINUTES} Minuten</span>
                <button type="button" className="rec-btn stop" onClick={recorder.stop} aria-label="Aufnahme beenden">
                  <Icon name="stop" />
                </button>
              </>
            )}
            {recorder.state === "idle" && (
              <>
                <span className="muted-s">Tippen, um die Aufnahme zu starten</span>
                <button type="button" className="rec-btn" onClick={() => void recorder.start()} aria-label="Aufnahme starten">
                  <Icon name="mic" />
                </button>
              </>
            )}
            {recorder.state === "done" && recorder.result && (
              <>
                <VoicePlayer src={recorder.result.url} seconds={recorder.result.seconds} seed="neu" />
                <button type="button" className="btn-text" onClick={recorder.reset}>
                  Verwerfen und neu aufnehmen
                </button>
              </>
            )}
            <Message>{recorder.error}</Message>
          </div>
        )}
        <label>
          {voice ? "Text dazu (freiwillig)" : "Notiz"}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={voice ? 3 : 6}
            maxLength={NOTE_MAX_CHARS}
            autoFocus={!voice}
            placeholder={voice ? "z. B. Aufmaß Küche" : "Was wurde besprochen?"}
          />
        </label>
        {!fixedProjectId && <ProjectSelect projects={projects} value={projectId} onChange={setProjectId} />}
        <Message>{error}</Message>
        <button className="app-btn" disabled={!canSave}>
          {busy ? "Wird gespeichert …" : "Speichern"}
        </button>
      </form>
    </Sheet>
  );
}

/* ---------- Kalender ---------- */

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function addDays(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}
function sameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}
/** Montag der Woche, in der der Monatserste liegt. */
function gridStart(month: Date) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  return addDays(first, -((first.getDay() + 6) % 7));
}
function onDay(e: EventInfo, day: Date) {
  const s = new Date(e.startsAt);
  const end = new Date(e.endsAt);
  return s < addDays(day, 1) && end >= day && !(end.getTime() === day.getTime() && s < day);
}
function dateInput(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function timeInput(d: Date) {
  return d.toTimeString().slice(0, 5);
}
function timeText(e: EventInfo) {
  if (e.allDay) return "Ganztägig";
  const s = new Date(e.startsAt);
  const end = new Date(e.endsAt);
  const t = (d: Date) => d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  return sameDay(s, end) ? `${t(s)} – ${t(end)}` : `${t(s)} bis ${end.toLocaleDateString("de-DE", { day: "numeric", month: "short" })} ${t(end)}`;
}

export function CalendarView({ me, projectId, nav }: { me: MeResponse; projectId?: string; nav?: ReactNode }) {
  const desk = useDesk();
  const onAuth = useUnauthorizedRedirect();
  const today = startOfDay(new Date());
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [day, setDay] = useState(today);
  const [events, setEvents] = useState<EventInfo[] | null>(null);
  const [error, setError] = useState("");
  const [edit, setEdit] = useState<{ event?: EventInfo } | null>(null);

  const first = gridStart(month);
  const days = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(first, i)), [first.getTime()]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    const from = days[0]!.toISOString();
    const to = addDays(days[41]!, 1).toISOString();
    api
      .get<EventInfo[]>(`/api/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${projectId ? `&projectId=${projectId}` : ""}`)
      .then(setEvents)
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [days, projectId, onAuth]);
  useEffect(load, [load]);

  const dayEvents = (events ?? []).filter((e) => onDay(e, day));
  const shift = (n: number) => {
    const m = new Date(month.getFullYear(), month.getMonth() + n, 1);
    setMonth(m);
    setDay(m.getMonth() === today.getMonth() && m.getFullYear() === today.getFullYear() ? today : m);
  };

  return (
    <>
      <main className="app-main cal-main">
        <div className="cal">
          <div className="cal-head">
            <button className="ab-btn" onClick={() => shift(-1)} aria-label="Voriger Monat">
              <Icon name="left" />
            </button>
            <b className="cal-month">
              {month.toLocaleDateString("de-DE", { month: "long" })}
              <span>{month.getFullYear()}</span>
            </b>
            <button className="ab-btn" onClick={() => shift(1)} aria-label="Nächster Monat">
              <Icon name="right" />
            </button>
          </div>
          <div className="cal-grid" role="grid">
            {WEEKDAYS.map((w) => (
              <span key={w} className="cal-wd">
                {w}
              </span>
            ))}
            {days.map((d) => {
              const count = (events ?? []).filter((e) => onDay(e, d)).length;
              const cls = [
                "cal-day",
                d.getMonth() !== month.getMonth() && "out",
                sameDay(d, today) && "today",
                sameDay(d, day) && "sel",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <button
                  key={d.getTime()}
                  className={cls}
                  onClick={() => setDay(d)}
                  aria-label={`${d.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })}${count ? `, ${count} Termine` : ""}`}
                  aria-pressed={sameDay(d, day)}
                >
                  {d.getDate()}
                  <i className="dots">
                    {Array.from({ length: Math.min(count, 3) }, (_, i) => (
                      <i key={i} />
                    ))}
                  </i>
                </button>
              );
            })}
          </div>
          {!sameDay(day, today) && (
            <button
              className="btn-text cal-today"
              onClick={() => {
                setMonth(new Date(today.getFullYear(), today.getMonth(), 1));
                setDay(today);
              }}
            >
              Heute
            </button>
          )}
        </div>

        <section className="cal-agenda">
          <div className="kick-line">
            <span>{String(day.getDate()).padStart(2, "0")}</span>
            <span>
              ▼ {day.toLocaleDateString("de-DE", { weekday: "long" })} · {dayEvents.length} {dayEvents.length === 1 ? "Termin" : "Termine"}
            </span>
          </div>
          <Message>{error}</Message>
          {events && dayEvents.length === 0 && <p className="app-empty small">Keine Termine an diesem Tag.</p>}
          <ul className="evlist">
            {dayEvents.map((e) => (
              <li key={e.id}>
                <button className="row ev-row" onClick={() => setEdit({ event: e })}>
                  <span className="ev-time">{timeText(e)}</span>
                  <span className="row-text">
                    <b>{e.title}</b>
                    <small>
                      {[e.location, !projectId && e.projectName, e.assignedName].filter(Boolean).join(" · ") || " "}
                    </small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {!projectId && <OutlookPanel onSynced={load} />}
          {!projectId && desk && <CalendarFeedPanel />}
        </section>
      </main>

      <Island>
        {nav}
        <button className="is-action" onClick={() => setEdit({})} aria-label="Neuer Termin">
          <Icon name="plus" />
        </button>
      </Island>

      {edit && (
        <EventSheet
          me={me}
          event={edit.event}
          day={day}
          fixedProjectId={projectId}
          onClose={() => setEdit(null)}
          onChanged={() => {
            setEdit(null);
            load();
          }}
        />
      )}
    </>
  );
}

function EventSheet(props: {
  me: MeResponse;
  event?: EventInfo;
  day: Date;
  fixedProjectId?: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { event, day, fixedProjectId, me } = props;
  const projects = useProjects(!fixedProjectId);
  const initialStart = event ? new Date(event.startsAt) : new Date(day.getFullYear(), day.getMonth(), day.getDate(), 8, 0);
  const initialEnd = event ? new Date(event.endsAt) : new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0);
  const [allDay, setAllDay] = useState(event?.allDay ?? false);
  const [startDate, setStartDate] = useState(dateInput(initialStart));
  const [startTime, setStartTime] = useState(timeInput(initialStart));
  const [endDate, setEndDate] = useState(dateInput(initialEnd));
  const [endTime, setEndTime] = useState(timeInput(initialEnd));
  const [projectId, setProjectId] = useState(event?.projectId ?? fixedProjectId ?? "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  const desk = useDesk();

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const start = allDay ? new Date(`${startDate}T00:00`) : new Date(`${startDate}T${startTime}`);
    const end = allDay ? new Date(new Date(`${endDate}T00:00`).getTime() + 86_400_000 - 1) : new Date(`${endDate}T${endTime}`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return setError("Bitte geben Sie Datum und Uhrzeit vollständig an.");
    const body = {
      title: String(f.get("title") ?? ""),
      startsAt: start.toISOString(),
      endsAt: end.toISOString(),
      allDay,
      location: String(f.get("location") ?? "") || null,
      details: String(f.get("details") ?? "") || null,
      projectId: projectId || null,
      assignedTo: String(f.get("assignedTo") ?? "") || null,
    };
    setBusy(true);
    setError("");
    try {
      if (event) await api.patch(`/api/events/${event.id}`, body);
      else await api.post("/api/events", body);
      props.onChanged();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  async function remove() {
    if (!event || !window.confirm(`Termin „${event.title}“ löschen?`)) return;
    try {
      await api.del(`/api/events/${event.id}`);
      props.onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  }

  if (event && sharing) return <ShareEventSheet event={event} onClose={() => setSharing(false)} />;

  if (event?.source === "outlook") {
    return (
      <Sheet title="Termin aus Outlook" onClose={props.onClose}>
        <div className="ev-readonly">
          <h3>{event.title}</h3>
          <p>
            {new Date(event.startsAt).toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })} · {timeText(event)}
          </p>
          {event.location && <p>{event.location}</p>}
          {event.details && <p className="muted-s">{event.details}</p>}
          <p className="hint small">Dieser Termin kommt aus Ihrem Outlook-Kalender. Ändern oder löschen Sie ihn dort, HandVero übernimmt das beim nächsten Abgleich.</p>
          {desk && (
            <button className="app-btn ghost" onClick={() => setSharing(true)}>
              Termin teilen
            </button>
          )}
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet title={event ? "Termin" : "Neuer Termin"} onClose={props.onClose}>
      <form className="app-form" onSubmit={save}>
        <label>
          Titel
          <input name="title" defaultValue={event?.title} required maxLength={200} autoFocus={!event} placeholder="z. B. Zählerschrank setzen" />
        </label>
        <label className="check">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
          Ganztägig
        </label>
        <div className="two">
          <label>
            Beginn
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                // Ende mitziehen, wenn es sonst vor dem Beginn läge.
                if (e.target.value > endDate) setEndDate(e.target.value);
                setStartDate(e.target.value);
              }}
              required
            />
          </label>
          {!allDay && (
            <label>
              Uhrzeit
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
            </label>
          )}
        </div>
        <div className="two">
          <label>
            Ende
            <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} required />
          </label>
          {!allDay && (
            <label>
              Uhrzeit
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
            </label>
          )}
        </div>
        <label>
          Ort (freiwillig)
          <input name="location" defaultValue={event?.location ?? ""} maxLength={200} />
        </label>
        <label>
          Wer (freiwillig)
          <select name="assignedTo" defaultValue={event?.assignedTo ?? ""}>
            <option value="">Niemand bestimmtes</option>
            {me.members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        {!fixedProjectId && <ProjectSelect projects={projects} value={projectId} onChange={setProjectId} />}
        <label>
          Notiz (freiwillig)
          <textarea name="details" defaultValue={event?.details ?? ""} rows={3} maxLength={5000} />
        </label>
        <Message>{error}</Message>
        <button className="app-btn" disabled={busy}>
          {busy ? "Wird gespeichert …" : "Speichern"}
        </button>
        {event && desk && (
          <button type="button" className="app-btn ghost" onClick={() => setSharing(true)}>
            Termin teilen
          </button>
        )}
        {event && (
          <button type="button" className="btn-text danger" onClick={() => void remove()}>
            Termin löschen
          </button>
        )}
      </form>
    </Sheet>
  );
}

/* ---------- Outlook ---------- */

const OUTLOOK_RESULT: Record<string, { ok: boolean; text: string }> = {
  verbunden: { ok: true, text: "Outlook ist verbunden. Ihre Termine gleichen sich ab jetzt in beide Richtungen ab." },
  abgebrochen: { ok: false, text: "Die Anmeldung bei Microsoft wurde abgebrochen. Sie können es jederzeit noch einmal versuchen." },
  fehler: { ok: false, text: "Die Verbindung mit Outlook hat nicht geklappt. Bitte versuchen Sie es noch einmal." },
  "nicht-eingerichtet": { ok: false, text: "Die Outlook-Anbindung wird bei HandVero gerade eingerichtet. Bitte versuchen Sie es später noch einmal." },
  support: { ok: false, text: "Outlook verbindet nur die Person selbst, nicht der Support." },
};

/** Verbindung mit dem eigenen Outlook-Kalender: verbinden, abgleichen, trennen. */
function OutlookPanel({ onSynced }: { onSynced: () => void }) {
  const [status, setStatus] = useState<OutlookStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(() => {
    const key = new URLSearchParams(window.location.search).get("outlook");
    return key ? (OUTLOOK_RESULT[key] ?? null) : null;
  });

  const sync = useCallback(async () => {
    setBusy(true);
    try {
      await api.post("/api/calendar/outlook/sync");
      onSynced();
    } catch (err) {
      setMsg({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
      api.get<OutlookStatus>("/api/calendar/outlook").then(setStatus, () => undefined);
    }
  }, [onSynced]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("outlook")) history.replaceState(null, "", window.location.pathname);
    api.get<OutlookStatus>("/api/calendar/outlook").then((s) => {
      setStatus(s);
      // Beim Öffnen nachziehen, was zuletzt in Outlook passiert ist.
      if (s.connected && (!s.lastSyncAt || Date.now() - new Date(s.lastSyncAt).getTime() > 5 * 60_000)) void sync();
    }, () => undefined);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function disconnect() {
    if (!window.confirm("Outlook trennen? Termine aus Outlook verschwinden dann hier. Ihre HandVero-Termine bleiben in beiden Kalendern stehen.")) return;
    setBusy(true);
    await api.del("/api/calendar/outlook").catch((err) => setMsg({ ok: false, text: errorText(err) }));
    setBusy(false);
    setStatus(await api.get<OutlookStatus>("/api/calendar/outlook"));
    onSynced();
  }

  if (!status) return null;
  const last = status.lastSyncAt ? new Date(status.lastSyncAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : null;
  return (
    <div className={`outlook${status.connected ? " on" : ""}`}>
      <span className="outlook-logo" aria-hidden="true">
        <Icon name="calendar" />
      </span>
      <div className="outlook-text">
        {!status.available && (
          <>
            <b>Outlook-Kalender</b>
            <small>Die Verbindung mit Outlook wird bei HandVero gerade eingerichtet.</small>
          </>
        )}
        {status.available && !status.connected && (
          <>
            <b>Mit Outlook verbinden</b>
            <small>Termine von hier landen in Ihrem Outlook-Kalender, Termine aus Outlook erscheinen hier.</small>
          </>
        )}
        {status.connected && (
          <>
            <b>Outlook verbunden</b>
            <small>
              {status.accountEmail}
              {last ? ` · abgeglichen ${last} Uhr` : ""}
            </small>
            {status.lastError && <small className="outlook-err">Letzter Abgleich: {status.lastError}</small>}
          </>
        )}
        {msg && <small className={msg.ok ? "outlook-ok" : "outlook-err"}>{msg.text}</small>}
      </div>
      <div className="outlook-actions">
        {status.available && !status.connected && (
          <a className="app-btn small" href={`/api/calendar/outlook/connect${window.location.pathname.startsWith("/app") ? "?ziel=app" : ""}`}>
            Verbinden
          </a>
        )}
        {status.connected && (
          <>
            <button className="app-btn small" onClick={() => void sync()} disabled={busy}>
              {busy ? "Gleicht ab …" : "Abgleichen"}
            </button>
            <button className="app-btn small ghost" onClick={() => void disconnect()} disabled={busy}>
              Trennen
            </button>
          </>
        )}
      </div>
    </div>
  );
}
